import { readWorkplaceCondition } from '../workplaceCondition/workplaceCondition';
import type { WorkflowImpactSummary } from '../../services/reportsApi';

export type SafetyCaseResultLabel =
  | 'improved'
  | 'unchanged'
  | 'worsened'
  | 'inconclusive'
  | 'followup_pending'
  | 'monitoring_continues';

export interface ClosureSummaryFields {
  what_was_observed?: string | null;
  what_changed?: string | null;
  what_followup_showed?: string | null;
  what_remains_open?: string | null;
  monitoring_note?: string | null;
}

export const CLOSURE_SUMMARY_FIELD_KEYS = [
  'what_was_observed',
  'what_changed',
  'what_followup_showed',
  'what_remains_open',
  'monitoring_note',
] as const;

export const LIFECYCLE_UX_LABELS: Record<string, string> = {
  open: 'Open',
  action_ready: 'Action ready',
  action_assigned: 'Action assigned',
  action_completed: 'Action completed',
  followup_pending: 'Follow-up pending',
  measurement_review: 'Measurement review',
  validated: 'Improvement validated',
  closed: 'Closed',
  archived: 'Archived',
};

export const RESULT_LABEL_DISPLAY: Record<SafetyCaseResultLabel, string> = {
  improved: 'Improved',
  unchanged: 'No meaningful change',
  worsened: 'Worsened',
  inconclusive: 'Inconclusive',
  followup_pending: 'Follow-up pending',
  monitoring_continues: 'Monitoring continues',
};

export const RESULT_STATUS_OPTIONS: Array<{
  value: SafetyCaseResultLabel;
  label: string;
}> = (Object.keys(RESULT_LABEL_DISPLAY) as SafetyCaseResultLabel[]).map((value) => ({
  value,
  label: RESULT_LABEL_DISPLAY[value],
}));

const CLOSURE_SUMMARY_SECTION_LABELS: Record<keyof ClosureSummaryFields, string> = {
  what_was_observed: 'What was observed',
  what_changed: 'What changed',
  what_followup_showed: 'What follow-up showed',
  what_remains_open: 'What remains open',
  monitoring_note: 'Monitoring note',
};

export function readClosureSummary(scope: unknown): ClosureSummaryFields {
  if (!scope || typeof scope !== 'object') return {};
  const closureSummary = (scope as Record<string, unknown>).closure_summary;
  if (!closureSummary || typeof closureSummary !== 'object') return {};
  return sanitizeClosureSummaryInput(closureSummary as ClosureSummaryFields);
}

export function sanitizeClosureSummaryInput(
  input?: ClosureSummaryFields | null,
): ClosureSummaryFields {
  const source = input && typeof input === 'object' ? input : {};
  const cleaned: ClosureSummaryFields = {};
  CLOSURE_SUMMARY_FIELD_KEYS.forEach((key) => {
    const value = String(source[key] || '').trim();
    if (value) cleaned[key] = value;
  });
  return cleaned;
}

export function hasClosureSummaryContent(fields: ClosureSummaryFields): boolean {
  return CLOSURE_SUMMARY_FIELD_KEYS.some((key) => String(fields[key] || '').trim());
}

export function resolveLifecycleUxLabel(status?: string | null): string {
  const key = String(status || '').trim().toLowerCase();
  if (key && LIFECYCLE_UX_LABELS[key]) return LIFECYCLE_UX_LABELS[key];
  if (!key) return 'Open';
  return key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function normalizeToken(value: unknown): string {
  return String(value || '').trim().toLowerCase();
}

export function readExplicitResultStatus(scope: unknown): SafetyCaseResultLabel | null {
  if (!scope || typeof scope !== 'object') return null;
  const explicitStatus = normalizeToken((scope as Record<string, unknown>).result_status);
  if (explicitStatus && explicitStatus in RESULT_LABEL_DISPLAY) {
    return explicitStatus as SafetyCaseResultLabel;
  }
  return null;
}

export function deriveSuggestedResultLabel(input: {
  safetyCaseStatus?: string | null;
  measurementStatus?: string | null;
  impactSummary?: WorkflowImpactSummary | null;
  followupMeasurement?: Record<string, unknown> | null;
  scope?: Record<string, unknown> | null;
}): SafetyCaseResultLabel | null {
  const scope = input.scope && typeof input.scope === 'object' ? { ...input.scope } : {};
  delete scope.result_status;
  return deriveResultLabel({
    ...input,
    scope,
  });
}

export function resolveResultStatusFromArtifact(
  persistedReport?: {
    report_payload?: Record<string, unknown> | null;
    report_content?: Record<string, unknown> | null;
    finalized_payload?: Record<string, unknown> | null;
  } | null,
): SafetyCaseResultLabel | null {
  const content = persistedReport?.report_content
    || persistedReport?.finalized_payload
    || persistedReport?.report_payload;
  if (!content || typeof content !== 'object') return null;
  return readExplicitResultStatus(content);
}

function hasFollowupMeasurementData(input: {
  impactSummary?: WorkflowImpactSummary | null;
  followupMeasurement?: Record<string, unknown> | null;
}): boolean {
  const impact = input.impactSummary || {};
  const followup = input.followupMeasurement || {};
  if (impact.followup_window?.start && impact.followup_window?.end) return true;
  if (impact.post_action_window?.start && impact.post_action_window?.end) return true;
  if (followup.followup_observed_count != null) return true;
  if (impact.followup_value != null || impact.post_action_exposure_count != null) return true;
  return false;
}

export function deriveResultLabel(input: {
  safetyCaseStatus?: string | null;
  measurementStatus?: string | null;
  impactSummary?: WorkflowImpactSummary | null;
  followupMeasurement?: Record<string, unknown> | null;
  scope?: Record<string, unknown> | null;
}): SafetyCaseResultLabel | null {
  const scope = input.scope && typeof input.scope === 'object' ? input.scope : {};
  const explicitStatus = normalizeToken((scope as Record<string, unknown>).result_status);
  if (explicitStatus && explicitStatus in RESULT_LABEL_DISPLAY) {
    return explicitStatus as SafetyCaseResultLabel;
  }

  const caseStatus = normalizeToken(input.safetyCaseStatus);
  const impact = input.impactSummary || {};
  const followup = input.followupMeasurement || {};
  const measurementStatus = normalizeToken(
    impact.measurement_status || input.measurementStatus || impact.confidence_level,
  );

  if (
    measurementStatus === 'insufficient_data'
    || impact.roi_classification === 'insufficient_data'
    || normalizeToken(impact.confidence_level) === 'insufficient_data'
  ) {
    return 'inconclusive';
  }

  if (!hasFollowupMeasurementData({ impactSummary: impact, followupMeasurement: followup })) {
    if (['followup_pending', 'measurement_review'].includes(caseStatus)) {
      return 'followup_pending';
    }
    if (['open', 'action_ready', 'action_assigned', 'action_completed'].includes(caseStatus)) {
      return 'followup_pending';
    }
  }

  const percentReduction = impact.percent_reduction ?? followup.rate_change_percent;
  if (percentReduction != null && Number.isFinite(Number(percentReduction))) {
    const value = Number(percentReduction);
    if (value > 0) return 'improved';
    if (value < 0) return 'worsened';
    return 'unchanged';
  }

  const roi = normalizeToken(impact.roi_classification);
  if (roi === 'positive_measured' || roi === 'positive_directional') return 'improved';
  if (roi === 'negative') return 'worsened';
  if (roi === 'neutral') return 'unchanged';

  const directionalChange = normalizeToken(followup.directional_change);
  if (directionalChange === 'decreased' || directionalChange === 'improved') return 'improved';
  if (directionalChange === 'increased' || directionalChange === 'worsened') return 'worsened';
  if (directionalChange === 'unchanged' || directionalChange === 'no_change') return 'unchanged';

  if (['open', 'action_ready', 'action_assigned', 'action_completed', 'followup_pending', 'measurement_review'].includes(caseStatus)) {
    return 'monitoring_continues';
  }

  if (caseStatus === 'validated' || caseStatus === 'closed') {
    return hasFollowupMeasurementData({ impactSummary: impact, followupMeasurement: followup })
      ? 'unchanged'
      : 'monitoring_continues';
  }

  return null;
}

export function resolveResultLabelDisplay(label: SafetyCaseResultLabel | null | undefined): string | null {
  if (!label) return null;
  return RESULT_LABEL_DISPLAY[label] || null;
}

export function buildDefaultClosureSummary(input: {
  scope?: Record<string, unknown> | null;
  safetyCaseStatus?: string | null;
  impactSummary?: WorkflowImpactSummary | null;
}): ClosureSummaryFields {
  const scope = input.scope && typeof input.scope === 'object' ? input.scope : {};
  const workplace = readWorkplaceCondition(scope);
  const observed = String(workplace.condition_summary || '').trim();
  const caseStatus = normalizeToken(input.safetyCaseStatus);
  const resultLabel = deriveResultLabel({
    safetyCaseStatus: caseStatus,
    impactSummary: input.impactSummary,
    scope,
  });

  const defaults: ClosureSummaryFields = {};
  if (observed) {
    defaults.what_was_observed = observed;
  }
  if (['action_completed', 'followup_pending', 'measurement_review', 'validated', 'closed'].includes(caseStatus)) {
    defaults.what_changed = 'Corrective actions were recorded for review on this Safety Case.';
  }
  if (resultLabel === 'improved') {
    defaults.what_followup_showed = 'Follow-up measurement indicates directional improvement without claiming individual causality.';
  } else if (resultLabel === 'unchanged') {
    defaults.what_followup_showed = 'Follow-up measurement did not show a meaningful directional change.';
  } else if (resultLabel === 'followup_pending') {
    defaults.what_followup_showed = 'Follow-up measurement is still pending for this case.';
  }
  if (resultLabel === 'monitoring_continues' || caseStatus === 'followup_pending') {
    defaults.monitoring_note = 'Monitoring continues while the closure loop remains open.';
  }
  return defaults;
}

export function closureSummarySections(fields: ClosureSummaryFields): Array<{ label: string; text: string }> {
  return CLOSURE_SUMMARY_FIELD_KEYS
    .map((key) => ({
      label: CLOSURE_SUMMARY_SECTION_LABELS[key],
      text: String(fields[key] || '').trim(),
    }))
    .filter((section) => section.text);
}
